import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import ExportButton from '../../components/ExportButton';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import { 
  Tablet, Plus, Search, Eye, Edit, Trash2, Copy, X,
  Building2, MapPin, Smartphone, Apple, Monitor, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';

const Tablets = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('micro', 'tablets');
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ field: null, dir: 'asc' });
  
  // Data states
  const [tablets, setTablets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0, 
    sin_estado: 0,
    by_os: [],
    by_brand: []
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', os_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], os: [], sites: [] });
  const [formOptions, setFormOptions] = useState({ empresas: [], sites: [], brands: [], statuses: [], os: [], ubicaciones: [], proveedores: [], usuarios: [], clientes: [] });
  
  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(tablets);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTablet, setSelectedTablet] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tabletToDelete, setTabletToDelete] = useState(null);

  // Fetch functions
  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/tablets/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/tablets/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/tablets/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchTablets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.os_id) params.append('os_id', filters.os_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.asset_tag_filter) params.append('asset_tag_filter', filters.asset_tag_filter);
      if (filters.usuario_filter) params.append('usuario_filter', filters.usuario_filter);
      if (filters.legajo_filter) params.append('legajo_filter', filters.legajo_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);
      if (sortConfig.field) {
        params.append('sort_by', sortConfig.field);
        params.append('sort_dir', sortConfig.dir);
      }
      
      const response = await api.get(`/assets/tablets?${params.toString()}`);
      setTablets(response.data.data);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.total, 
        pages: response.data.pages 
      }));
    } catch (error) {
      console.error('Error fetching tablets:', error);
      toast.error('Error al cargar tablets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchTablets();
  }, [pagination.page, pagination.limit, sortConfig]);

  // Debounced filter fetch (800ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTablets();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) return <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />;
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" /> 
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', os_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({});
    setShowModal(true);
  };

  const openEditModal = (tablet) => {
    setModalMode('edit');
    setSelectedTablet(tablet);
    setFormData({
      empresa_id: tablet.empresa_id || '',
      site_id: tablet.site_id || '',
      brand_id: tablet.brand_id || '',
      status_id: tablet.status_id || '',
      asset_tag: tablet.asset_tag || '',
      serial_number: tablet.serial_number || '',
      modelo: tablet.modelo || '',
      os_id: tablet.os_id || '',
      ip_address: tablet.ip_address || '',
      location_id: tablet.location_id || '',
      supplier_id: tablet.supplier_id || '',
      purchase_date: tablet.purchase_date || '',
      warranty_end: tablet.warranty_end || '',
      assigned_user_id: tablet.assigned_user_id || '',
      cliente_id: tablet.cliente_id || '',
      imei: tablet.imei || '',
      phone_number: tablet.phone_number || '',
      storage_gb: tablet.storage_gb || '',
      ram_gb: tablet.ram_gb || '',
      comments: tablet.comments || ''
    });
    setShowModal(true);
  };

  const openCopyModal = (tablet) => {
    setModalMode('create');
    setFormData({
      empresa_id: tablet.empresa_id || '',
      site_id: tablet.site_id || '',
      brand_id: tablet.brand_id || '',
      status_id: tablet.status_id || '',
      modelo: tablet.modelo || '',
      os_id: tablet.os_id || '',
      location_id: tablet.location_id || '',
      supplier_id: tablet.supplier_id || '',
      storage_gb: tablet.storage_gb || '',
      ram_gb: tablet.ram_gb || ''
    });
    setShowModal(true);
    toast.info('Datos copiados - Complete los campos únicos');
  };

  const handleSubmit = async () => {
    try {
      // Clean form data - convert empty strings to null for IDs
      const cleanData = { ...formData };
      ['assigned_user_id', 'empresa_id', 'site_id', 'brand_id', 'status_id', 'os_id', 'location_id', 'supplier_id'].forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === 'none') {
          cleanData[field] = null;
        } else if (cleanData[field]) {
          cleanData[field] = parseInt(cleanData[field], 10) || null;
        }
      });
      
      if (modalMode === 'create') {
        await api.post('/assets/tablets', cleanData);
        toast.success('Tablet creada exitosamente');
      } else {
        await api.put(`/assets/tablets/${selectedTablet.id}`, cleanData);
        toast.success('Tablet actualizada exitosamente');
      }
      setShowModal(false);
      fetchTablets();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/tablets/${tabletToDelete.id}`);
      toast.success('Tablet eliminada exitosamente');
      setShowDeleteModal(false);
      setTabletToDelete(null);
      fetchTablets();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Tablet className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Tablets</h1>
              <p className="text-sm text-muted-foreground">Gestión de tablets corporativas</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/tablets"
              filters={filters}
              filename="tablets"
            />
            {canCreate && (
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nueva Tablet
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Estilo Workstations */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="tablet-stats">
          {/* Total */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Tablet className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          
          {/* Top OS 1 */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-os-1">
            <Smartphone className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">
              {stats.by_os?.[0]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_os?.[0]?.name || 'Sin OS'}</div>
            </div>
          </div>
          
          {/* Top OS 2 */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-os-2">
            <Smartphone className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">
              {stats.by_os?.[1]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_os?.[1]?.name || 'Otros SO'}</div>
            </div>
          </div>
          
          {/* Top Marca 1 */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-brand-1">
            <Monitor className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">
              {stats.by_brand?.[0]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_brand?.[0]?.name || 'Sin Marca'}</div>
            </div>
          </div>
          
          {/* Top Marca 2 */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-brand-2">
            <Monitor className="h-6 w-6 text-orange-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-orange-500">
              {stats.by_brand?.[1]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_brand?.[1]?.name || 'Otras Marcas'}</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-10 py-3 px-4">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isSomeSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('asset_tag')}>
                      <div className="flex items-center gap-1">ASSET TAG <SortIcon field="asset_tag" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('brand_name')}>
                      <div className="flex items-center gap-1">MARCA <SortIcon field="brand_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('cliente_name')}>
                      <div className="flex items-center gap-1">CLIENTE <SortIcon field="cliente_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('os_name')}>
                      <div className="flex items-center gap-1">SO <SortIcon field="os_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('status_name')}>
                      <div className="flex items-center gap-1">ESTADO <SortIcon field="status_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('user_name')}>
                      <div className="flex items-center gap-1">USUARIO <SortIcon field="user_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('usuario_legajo')}>
                      <div className="flex items-center gap-1">LEGAJO <SortIcon field="usuario_legajo" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('site_name')}>
                      <div className="flex items-center gap-1">SITE <SortIcon field="site_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('location_name')}>
                      <div className="flex items-center gap-1">UBICACIÓN <SortIcon field="location_name" /></div>
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-28">ACCIONES</th>
                  </tr>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="w-10 py-2 px-4"></th>
                    <th className="py-2 px-4"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.asset_tag_filter} onChange={(e) => handleFilterChange('asset_tag_filter', e.target.value)} /></th>
                    <th className="py-2 px-4">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.brands?.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {formOptions.clientes?.map(c => (<SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.os_id || undefined} onValueChange={(v) => handleFilterChange('os_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.os?.map(o => (<SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.statuses?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                          <SelectItem value="null">Sin estado</SelectItem>
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.usuario_filter} onChange={(e) => handleFilterChange('usuario_filter', e.target.value)} /></th>
                    <th className="py-2 px-4 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.legajo_filter} onChange={(e) => handleFilterChange('legajo_filter', e.target.value)} /></th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.sites?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} /></th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8 text-muted-foreground">
                        Cargando...
                      </td>
                    </tr>
                  ) : tablets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8 text-muted-foreground">
                        No se encontraron tablets
                      </td>
                    </tr>
                  ) : (
                    tablets.map((tablet) => (
                      <tr key={tablet.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${isSelected(tablet.id) ? 'bg-primary/5' : ''}`}>
                        <td className="w-10 py-3 px-4">
                          <Checkbox
                            checked={isSelected(tablet.id)}
                            onCheckedChange={() => toggleRow(tablet.id)}
                            aria-label={`Seleccionar ${tablet.asset_tag}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{tablet.asset_tag || '-'}</td>
                        <td className="py-3 px-4">{tablet.brand_name || '-'}</td>
                        <td className="py-3 px-4">{tablet.cliente_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{tablet.os_name?.trim() || '-'}</td>
                        <td className="py-3 px-4">{getStatusBadge(tablet.status_name)}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{tablet.assigned_user_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{tablet.usuario_legajo || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{tablet.site_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{tablet.location_name || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/micro/tablets/${tablet.id}`)}
                              title="Ver detalle"
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(tablet)}
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openCopyModal(tablet)}
                                title="Copiar"
                              >
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => { setTabletToDelete(tablet); setShowDeleteModal(true); }}
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <Select
                      value={pagination.limit.toString()}
                      onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                    >
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tablet className="h-5 w-5" strokeWidth={1.5} />
                {modalMode === 'create' ? 'Nueva Tablet' : 'Editar Tablet'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input
                  value={formData.asset_tag || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                  placeholder="Ej: TAB-001"
                  className={modalMode === 'create' && !formData.asset_tag ? 'border-yellow-500' : ''}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  data-validation={modalMode === 'create' && !formData.serial_number ? 'required-empty' : ''}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={String(formData.brand_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.brand_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input
                  value={formData.modelo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  data-validation={modalMode === 'create' && formData.modelo ? 'valid' : ''}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Sistema Operativo</Label>
                <Select value={String(formData.os_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, os_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.os_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.os?.map(o => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={String(formData.status_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.status_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Select value={String(formData.empresa_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.empresa_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Site</Label>
                <Select value={String(formData.site_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.site_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select value={String(formData.location_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.location_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={String(formData.supplier_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.supplier_id ? 'valid' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input
                  value={formData.ip_address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Usuario Asignado</Label>
                <Select
                  value={formData.assigned_user_id ? formData.assigned_user_id.toString() : 'none'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_user_id: v === 'none' ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar usuario..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {formOptions.usuarios?.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select
                  value={formData.cliente_id?.toString() || 'none'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v === 'none' ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>IMEI</Label>
                <Input
                  value={formData.imei || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, imei: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Número de Teléfono</Label>
                <Input
                  value={formData.phone_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Almacenamiento (GB)</Label>
                <Input
                  type="number"
                  value={formData.storage_gb || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, storage_gb: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>RAM (GB)</Label>
                <Input
                  type="number"
                  value={formData.ram_gb || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ram_gb: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fin de Garantía</Label>
                <Input
                  type="date"
                  value={formData.warranty_end || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, warranty_end: e.target.value }))}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>
                {modalMode === 'create' ? 'Crear' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Tablet
              </DialogTitle>
            </DialogHeader>
            <p>¿Estás seguro de eliminar la tablet <strong>{tabletToDelete?.asset_tag || tabletToDelete?.serial_number}</strong>?</p>
            <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchTablets(); fetchStats(); }}
          endpoint="/assets/tablets"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.statuses,
            ubicaciones: formOptions.ubicaciones,
            usuarios: formOptions.usuarios
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id', 'assigned_user_id']}
        />
      </div>
    </Layout>
  );
};

export default Tablets;
